import { AnimatedContentLoader, DoubleColumnLayout, DoubleColumnLayoutColumn } from '@components'
import { useBusinessContext } from '@context'
import { useAccountStruct, useLocalStorage, useQueryParam } from '@hooks'
import { fetchAccounts, hideAccount, IAccount, showAccount, updateAccount } from '@query'
import React, { useState } from 'react'
import useVirtual from 'react-cool-virtual'
import { useTranslation } from 'react-i18next'
import { FaCaretRight } from 'react-icons/fa'
import { TextTooltip } from '@components'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import styled from 'styled-components'
import { AccountActionBar, AccountDetails } from '../../components'
import { TabularAccountTable } from './TabularAccountTable'
import { IFilter } from '@root/components/ActionBar/FilterBar/FilterSelection'

const HEADER_HEIGHT = 50
const TABLE_ROW_HEIGHT = 45
const TABLE_HEADER_HEIGHT = 35

export interface IUpdateTabularAccountProps {
  accountId: number
  data: Partial<IAccount>
}

export const TabularAccountPage: React.FC = () => {
  const {
    t,
    i18n: { language }
  } = useTranslation()
  const { slug } = useBusinessContext()
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [filters, setFilter] = useLocalStorage<IFilter[]>(`${slug}.filters.account`, [])
  const [accountId, setAccountId] = useQueryParam<number>('id', value => parseInt(value, 10))

  const queryParams = {
    all: true,
    search,
    ...filters?.reduce(
      (prev, current) => ({
        ...prev,
        ...current
      }),
      {}
    )
  }
  const { data, isLoading } = useQuery({
    queryKey: [slug, 'tabular_accounts', queryParams, language],
    queryFn: () => fetchAccounts({ slug }, { page_size: 99999, ...queryParams })
  })
  const accountStruct = useAccountStruct(data?.results || [])

  const updateMutation = useMutation<IAccount, unknown, IUpdateTabularAccountProps>({
    mutationFn: ({ accountId, data }) => updateAccount({ slug, accountId }, data),
    onSuccess: updatedAccount => {
      queryClient.invalidateQueries({ queryKey: [slug, 'accounts'] })
      queryClient.invalidateQueries({ queryKey: [slug, 'auto_complete_accounts'] })
      queryClient.setQueriesData(
        { queryKey: [slug, 'tabular_accounts'] },
        ({ results: oldResults, ...rest }) => ({
          results: oldResults.map(account =>
            account.id === updatedAccount.id ? updatedAccount : account
          ),
          ...rest
        })
      )
    }
  })

  const toggleVisibilityMutation = useMutation<
    void,
    unknown,
    { accountId: number; isShown: boolean }
  >({
    mutationFn: ({ accountId, isShown }) =>
      isShown ? showAccount({ slug, accountId }) : hideAccount({ slug, accountId }),
    onSuccess: (_, { accountId, isShown }) => {
      queryClient.invalidateQueries({ queryKey: [slug, 'accounts'] })
      queryClient.invalidateQueries({ queryKey: [slug, 'auto_complete_accounts'] })
      queryClient.setQueriesData(
        { queryKey: [slug, 'tabular_accounts'] },
        ({ results: oldResults, ...rest }) => ({
          results: oldResults.map(account =>
            account.id === accountId ? { ...account, is_shown: isShown } : account
          ),
          ...rest
        })
      )
    }
  })

  const updateData = (data: IUpdateTabularAccountProps) => updateMutation.mutateAsync(data)
  const toggleVisibility = (accountId: number, isShown: boolean) =>
    toggleVisibilityMutation.mutateAsync({ accountId, isShown })

  const { outerRef, innerRef, items } = useVirtual<HTMLDivElement, HTMLDivElement>({
    itemCount: accountStruct.length,
    itemSize: index => {
      const header = accountStruct[index]
      return HEADER_HEIGHT + header.accounts.length * TABLE_ROW_HEIGHT + TABLE_HEADER_HEIGHT
    }
  })

  const renderRow = React.useCallback(({ index, accountStruct }) => {
    // Virtual list and react query combination has a side effect where
    // number of virtual items can temporarily differ from number of
    // accountStruct items. This works around that issue.
    if (!accountStruct[index]) return

    const { path, accounts } = accountStruct[index]
    const key = [...path, index].join('-')

    return (
      <React.Fragment key={key}>
        <TextTooltip
          openDelay={500}
          closeDelay={0}
          maxWidth={400}
          tooltip={
            <PathInTooltip>
              {path.map((p, i) => (
                <div key={`path-${i}`}>
                  <FaCaretRight size={12} />
                  <span>{p}</span>
                </div>
              ))}
            </PathInTooltip>
          }
          placement="bottom-start"
        >
          <StyledHeaderWrapper>
            {path.map((p, i) => (
              <React.Fragment key={`path-${i}`}>
                <HeaderBreadcrumb>{p}</HeaderBreadcrumb>
                {i < path.length - 1 && <FaCaretRight size={12} />}
              </React.Fragment>
            ))}
          </StyledHeaderWrapper>
        </TextTooltip>

        <TabularAccountTable
          accounts={accounts}
          rowHeight={TABLE_ROW_HEIGHT}
          headerHeight={TABLE_HEADER_HEIGHT}
          updateData={updateData}
          toggleVisibility={toggleVisibility}
        />
      </React.Fragment>
    )
  }, [])

  return (
    <DoubleColumnLayout
      header={t('accounts.title')}
      isRightVisible={Boolean(accountId)}
      forceOverlayMode={true}
      onRightClose={() => setAccountId(undefined)}
    >
      <DoubleColumnLayoutColumn>
        <PageWrapper>
          <AccountActionBar
            onSearch={search => setSearch(search)}
            onFilter={filters => setFilter(filters)}
            activeFilters={filters}
          />

          <PageContent ref={outerRef}>
            <AnimatedContentLoader
              isLoading={isLoading}
              isEmpty={accountStruct.length === 0}
              isEmptyDescription={t('accounts.empty')}
            >
              <div ref={innerRef}>
                {items.map(({ index }) => renderRow({ index, accountStruct }))}
              </div>
            </AnimatedContentLoader>
          </PageContent>
        </PageWrapper>
      </DoubleColumnLayoutColumn>

      <DoubleColumnLayoutColumn isRight={true} innerKey={`account-${accountId}`}>
        {accountId && <AccountDetails accountId={accountId} />}
      </DoubleColumnLayoutColumn>
    </DoubleColumnLayout>
  )
}

const StyledHeaderWrapper = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  height: ${HEADER_HEIGHT}px;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.xs}rem;
  width: fit-content;
`

const HeaderBreadcrumb = styled.div`
  align-self: center;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  color: ${({ theme }) => theme.colors.metalGray};
  max-width: 100%;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
`

const PathInTooltip = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  font-size: ${({ theme }) => theme.fontSize.md}rem;

  & > svg {
    margin: 0 ${({ theme }) => theme.spacing.xs}rem;
  }
`

const PageWrapper = styled.div`
  height: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
`

const PageContent = styled.div`
  flex: 1;
  height: 100%;
  overflow: auto;
  position: relative;
`
